<template>
  <!-- ICONIC: Protokoll in Klartext – was über die Weboberfläche und auf dem Server geändert wurde -->
  <v-container class="protokoll">
    <div class="d-flex flex-wrap align-end mb-3">
      <div class="mr-auto mb-2">
        <h1 class="text-h5 font-weight-medium">Protokoll</h1>
        <div class="text-body-2 text--secondary">Wer hat wann was gemacht.</div>
      </div>
      <v-btn text class="mb-2" :loading="loading" @click="load"><v-icon left>mdi-refresh</v-icon>Neu laden</v-btn>
    </div>

    <v-btn-toggle v-model="tab" mandatory dense class="mb-3">
      <v-btn :value="0">Weboberfläche</v-btn>
      <v-btn :value="1">TeamSpeak</v-btn>
    </v-btn-toggle>

    <div class="d-flex flex-wrap align-center mb-3">
      <v-text-field
        v-model="search"
        prepend-inner-icon="mdi-magnify"
        label="Name oder Wort suchen"
        clearable
        hide-details
        outlined
        dense
        class="search mr-3 mb-2"
      ></v-text-field>
      <v-chip-group v-model="kind" mandatory class="mb-2">
        <v-chip v-for="k in kinds" :key="k.value" :value="k.value" filter outlined small>{{ k.text }}</v-chip>
      </v-chip-group>
    </div>

    <v-alert
      v-if="tab === 1 && logOff"
      type="info"
      outlined
      dense
      class="text-body-2"
    >
      TeamSpeak schreibt im Moment nur Änderungen an Rechten und Rollen mit.
      Rauswürfe, Banns, Kanäle und Kommen/Gehen fehlen deshalb hier.
      <template #append>
        <v-btn small text color="primary" @click="askLog = true">Mitschreiben einschalten</v-btn>
      </template>
    </v-alert>

    <v-card outlined>
      <v-progress-linear v-if="loading" indeterminate></v-progress-linear>
      <p v-if="!loading && !visible.length" class="text-body-2 text--secondary pa-4 mb-0">
        {{ tab === 0 ? "Noch keine Änderungen über die Weboberfläche." : "Keine passenden Einträge." }}
      </p>
      <template v-for="(group, gi) in grouped">
        <v-subheader :key="`h${gi}`" class="font-weight-medium">{{ group.day }}</v-subheader>
        <div v-for="(e, i) in group.items" :key="`e${gi}-${i}`" class="entry d-flex px-4 py-2">
          <span class="time text-caption text--secondary mr-3">{{ e.clock }}</span>
          <v-icon small class="mr-2" :color="e.ok === false ? 'error' : undefined">{{ icon(e.kind, e.ok) }}</v-icon>
          <span class="text-body-2">
            {{ e.text }}
            <span v-if="e.ok === false" class="error--text"> – fehlgeschlagen{{ e.error ? `: ${e.error}` : "" }}</span>
            <span v-if="e.user" class="text-caption text--secondary"> · {{ e.user }}</span>
          </span>
        </div>
      </template>
    </v-card>
    <p v-if="tab === 1" class="text-caption text--secondary mt-2">
      Zeigt die letzten rund 1000 Zeilen des Server-Logs ohne technische Verbindungsmeldungen. IP-Adressen sind ausgeblendet.
    </p>

    <v-dialog v-model="askLog" max-width="460">
      <v-card>
        <v-card-title>Mitschreiben einschalten?</v-card-title>
        <v-card-text class="text-body-1">
          TeamSpeak schreibt dann auch Rauswürfe, Banns, Kanal-Änderungen und
          wer kommt und geht ins Server-Log. Niemand merkt etwas davon, kein Neustart.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="askLog = false">Abbrechen</v-btn>
          <v-btn color="primary" depressed :loading="busy" @click="enableLog">Einschalten</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { ensureServer, asList, resolveGroups, channelRoleLabel, errorText } from "@/iconic/rechte";
import { describeAction, parseLogLine, KINDS } from "@/iconic/protokoll";

const ICONS = { role: "mdi-account-key", kick: "mdi-cancel", channel: "mdi-pound", join: "mdi-login", other: "mdi-information-outline" };

export default {
  data() {
    return {
      tab: 0,
      kind: "all",
      kinds: KINDS,
      search: "",
      loading: true,
      busy: false,
      askLog: false,
      actions: [],
      logEntries: [],
      logOff: false,
    };
  },
  computed: {
    visible() {
      const list = this.tab === 0 ? this.actions : this.logEntries;
      const s = String(this.search || "").toLowerCase().trim();
      return list.filter((e) => (this.kind === "all" || e.kind === this.kind) && (!s || e.text.toLowerCase().includes(s)));
    },
    grouped() {
      const out = [];
      this.visible.forEach((e) => {
        const last = out[out.length - 1];
        if (last && last.day === e.day) last.items.push(e);
        else out.push({ day: e.day, items: [e] });
      });
      return out;
    },
  },
  methods: {
    icon(kind, ok) {
      return ok === false ? "mdi-alert-circle-outline" : ICONS[kind] || ICONS.other;
    },
    stamp(date) {
      const d = date instanceof Date ? date : new Date(date);
      if (isNaN(d)) return { day: "", clock: "" };
      return {
        day: d.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" }),
        clock: d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }),
      };
    },
    actionsRequest() {
      return new Promise((resolve) => this.$socket.emit("iconic-actions", (r) => resolve((r && r.entries) || [])));
    },
    async readLog() {
      const lines = [];
      let pos;
      for (let i = 0; i < 10; i++) {
        const page = asList(
          await this.$TeamSpeak.execute("logview", { lines: 100, reverse: 1, instance: 0, ...(pos !== undefined ? { begin_pos: pos } : {}) })
        );
        if (!page.length) break;
        lines.push(...page.map((x) => x.l));
        pos = page[0].lastPos;
        if (!pos) break;
      }
      return lines;
    },
    async load() {
      this.loading = true;
      try {
        await ensureServer();
        const ts = this.$TeamSpeak;
        const [serverInfo, serverGroups, channelGroups, entries, lines] = await Promise.all([
          ts.execute("serverinfo").then((r) => asList(r)[0]),
          ts.execute("servergrouplist").then(asList),
          ts.execute("channelgrouplist").then(asList),
          this.actionsRequest(),
          this.readLog(),
        ]);
        const groups = resolveGroups(serverInfo, serverGroups, channelGroups);
        const lookup = {
          sgName: (id) => groups.sgName(id),
          cgLabel: (id) => channelRoleLabel(groups, id),
        };
        this.logOff = !(Number(serverInfo.virtualserverLogClient) && Number(serverInfo.virtualserverLogChannel));
        this.actions = entries.map((e) => ({ ...describeAction(e, lookup), ...this.stamp(e.t), ok: e.ok, error: e.error, user: e.user }));
        this.logEntries = lines
          .map(parseLogLine)
          .filter(Boolean)
          .map((e) => ({ ...e, ...this.stamp(e.time.replace(" ", "T").replace(/\.\d+$/, "") + "Z") }))
          .sort((a, b) => (a.time < b.time ? 1 : -1));
      } catch (err) {
        this.$toast.error(errorText(err));
      }
      this.loading = false;
    },
    async enableLog() {
      this.busy = true;
      try {
        await this.$TeamSpeak.execute("serveredit", {
          virtualserver_log_client: 1,
          virtualserver_log_channel: 1,
          virtualserver_log_server: 1,
        });
        this.$toast.success("TeamSpeak schreibt jetzt mehr mit.");
        this.askLog = false;
      } catch (err) {
        this.$toast.error(errorText(err));
      }
      this.busy = false;
      await this.load();
    },
  },
  created() {
    this.load();
  },
};
</script>

<style scoped>
.protokoll {
  max-width: 980px;
}
.search {
  max-width: 280px;
}
.time {
  min-width: 40px;
  padding-top: 2px;
}
.entry + .entry {
  border-top: 1px solid rgba(128, 128, 128, 0.15);
}
</style>
